import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEOHelmet } from '@/components/SEOHelmet';

export default function KVKK() {
  const navigate = useNavigate();

  return (
    <>
      <SEOHelmet
        title="KVKK Aydınlatma Metni - Remindora | Kişisel Verilerin Korunması"
        description="Remindora KVKK aydınlatma metni. Kişisel verilerinizin nasıl toplandığı, işlendiği ve korunduğu hakkında bilgi."
        keywords="kvkk, kişisel verilerin korunması, gizlilik, veri güvenliği, remindora"
        path="/kvkk"
      />
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50/30 to-neutral-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-neutral-200 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">KVKK Aydınlatma Metni</h1>
                <p className="text-sm text-neutral-600">Kişisel Verilerin Korunması ve İşlenmesi Politikası</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 space-y-8">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">1. Veri Sorumlusu</h2>
              <p className="text-neutral-700 leading-relaxed mb-3">
                Kişisel verileriniz, veri sorumlusu sıfatıyla <strong>Remindora Teknoloji</strong> tarafından işlenmektedir.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
                <p className="text-sm"><strong>Adres:</strong> Bahçeşehir 2. Kısım. Eminkent Evleri</p>
                <p className="text-sm"><strong>E-posta:</strong> bilgilendirme@remindora.io</p>
                <p className="text-sm"><strong>Web:</strong> remindora.io</p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">2. İşlenen Kişisel Veriler</h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Remindora hizmetlerini kullanmanız halinde aşağıdaki veriler işlenebilir:
              </p>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span><strong>Kimlik Bilgileri</strong> (ad, soyad, doğum tarihi, partner adı)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span><strong>İletişim Bilgileri</strong> (e-posta, telefon numarası – isteğe bağlı)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span><strong>Özel Gün Bilgileri</strong> (doğum günü, yıldönümü, nişan tarihi vb.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span><strong>Kullanıcı Tercihleri</strong> (ilgi alanları, bütçe, öneri geçmişi)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">•</span>
                  <span><strong>Teknik Bilgiler</strong> (IP adresi, cihaz, çerez verisi)</span>
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">3. Verilerin İşlenme Amaçları</h2>
              <p className="text-neutral-700 leading-relaxed mb-3">Kişisel verileriniz;</p>
              <ul className="space-y-2 text-neutral-700 ml-4">
                <li>✓ Üyelik oluşturma, giriş yapma ve hizmetten yararlanma süreçlerinin yürütülmesi,</li>
                <li>✓ Özel gün hatırlatma, e-posta bildirimleri ve yapay zekâ destekli önerilerin sunulması,</li>
                <li>✓ Sistem güvenliğinin sağlanması ve kullanıcı deneyiminin iyileştirilmesi,</li>
                <li>✓ Yasal yükümlülüklerin yerine getirilmesi</li>
              </ul>
              <p className="text-neutral-700 leading-relaxed mt-3">amaçlarıyla işlenmektedir.</p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">4. Veri Güvenliği ve Gizlilik Garantisi 🛡️</h2>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-5 space-y-3">
                <p className="text-neutral-800 leading-relaxed">
                  <strong>Remindora olarak</strong> kişisel verilerinizin gizliliğini korumak için tüm teknik ve idari önlemleri alıyoruz.
                </p>
                <ul className="space-y-2 text-neutral-700">
                  <li>🔒 Verileriniz güvenli sunucularda şifrelenmiş şekilde saklanır</li>
                  <li>🚫 Hiçbir koşulda üçüncü kişilerle paylaşılmaz</li>
                  <li>👤 Sisteme yalnızca yetkili personel erişebilir</li>
                  <li>📋 Tüm erişimler kayıt altına alınır ve düzenli denetlenir</li>
                  <li>💎 Verileriniz saklı kalır, kimse erişemez</li>
                </ul>
                <p className="text-neutral-800 font-semibold">
                  Remindora, kullanıcı verilerini hiçbir şekilde satmaz, kiralamaz veya ticari amaçla devretmez.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">5. Verilerin Aktarımı</h2>
              <p className="text-neutral-700 leading-relaxed mb-3">
                Verileriniz yalnızca hizmetin teknik gereklilikleri doğrultusunda;
              </p>
              <ul className="space-y-2 text-neutral-700 ml-4">
                <li>• Bulut bilişim servis sağlayıcılarına (veri güvenliği standartlarına uygun olarak),</li>
                <li>• Yasal mercilere (zorunlu hallerde)</li>
              </ul>
              <p className="text-neutral-700 leading-relaxed mt-3">
                aktarılabilir. Verileriniz yurt dışına aktarılmadan önce açık rızanız alınır.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">6. Açık Rıza Metni</h2>
              <p className="text-neutral-700 leading-relaxed mb-3">
                Aşağıdaki amaçlarla kişisel verilerimin işlenmesine açık rıza gösteriyorum:
              </p>
              <ul className="space-y-2 text-neutral-700 ml-4">
                <li>✓ Özel gün hatırlatmaları, e-posta ve SMS gönderimleri yapılması,</li>
                <li>✓ Yapay zekâ destekli hediye ve mekan önerilerinin sunulması,</li>
                <li>✓ Kullanıcı deneyiminin kişiselleştirilmesi,</li>
                <li>✓ Verilerin yurt içindeki veya yurt dışındaki hizmet sağlayıcılara aktarılması.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">7. İletişim Onayı (Opsiyonel)</h2>
              <p className="text-neutral-700 leading-relaxed">
                Remindora tarafından özel günlere ilişkin hatırlatma ve kampanya mesajlarının e-posta veya SMS yoluyla gönderilmesine de rıza gösteriyorum.
                İstediğim zaman bu onayı geri çekebilirim.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">8. Rızanın Geçerliliği ve Geri Alınması</h2>
              <p className="text-neutral-700 leading-relaxed mb-2">
                Açık rızam hizmetten yararlandığım süre boyunca geçerlidir.
              </p>
              <p className="text-neutral-700 leading-relaxed">
                Dilediğim anda <strong>bilgilendirme@remindora.io</strong> adresine e-posta göndererek veya uygulama ayarlarından açık rızamı geri çekebilirim.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">9. Haklarınız (KVKK m.11)</h2>
              <p className="text-neutral-700 leading-relaxed mb-3">Kişisel verilerinizle ilgili haklarınız:</p>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Kişisel verilerinizin işlenip işlenmediğini öğrenme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>İşlenmişse buna ilişkin bilgi talep etme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Amacına uygun kullanılıp kullanılmadığını öğrenme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Eksik veya yanlış işlenmişse düzeltilmesini isteme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Silinmesini veya anonim hale getirilmesini talep etme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Verilerin yurt içinde veya dışında aktarıldığı üçüncü kişileri öğrenme,</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">→</span>
                  <span>Zarara uğramanız hâlinde tazminat talep etme hakkına sahipsiniz.</span>
                </li>
              </ul>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mb-4">10. Güncellemeler</h2>
              <p className="text-neutral-700 leading-relaxed">
                Bu metin zaman zaman güncellenebilir. Güncel sürüm her zaman <strong>www.remindora.com/kvkk</strong> adresinde yayınlanır.
              </p>
            </section>

            {/* Footer Info */}
            <section className="border-t pt-6 mt-8">
              <div className="bg-neutral-100 rounded-lg p-4 space-y-2 text-sm text-neutral-700">
                <p><strong>📅 Yürürlük Tarihi:</strong> 11 Kasım 2025</p>
                <p><strong>📍 Veri Sorumlusu:</strong> Remindora Teknoloji</p>
                <p><strong>📧 İletişim:</strong> bilgilendirme@remindora.io</p>
                <p><strong>🌐 Web:</strong> remindora.io</p>
              </div>
            </section>

          </div>
        </main>
      </div>
    </>
  );
}
